import { Currency } from "@redotech/money/currencies";
import { useLoad } from "@redotech/react-util/load";
import { StyleShadowRoot } from "@redotech/react-util/style";
import { getCoverageCreditAmount } from "@redotech/redo-model/coverage-calculator/coverage-credit-incentive";
import { StorefrontCheckoutTextUtils } from "@redotech/redo-model/storefront-checkout-text-utils";
import { isAttachmentStrategyUsingCheckoutButtons } from "@redotech/redo-model/team";
import {
  CoverageInfo,
  WidgetAutocheck,
  WidgetConfig,
  WidgetElementType,
  WidgetFees,
} from "@redotech/redo-model/widget";
import { ModalRootContext } from "@redotech/redo-web/modal-root";
import {
  ExtensionShopifyClient,
  ShopifyCart,
} from "@redotech/shopify-client/ajax";
import { isDefined } from "@redotech/util/type";
import { memo, useEffect, useRef, useState } from "react";
import { RedoToggleElement } from "../cart-app";
import { getTryFirstTotals } from "../cart-optimization/cart-opt-modifications";
import { CheckoutButtons } from "../checkout-buttons";
import { ShopifyExtensionClient } from "../client/shopify-extension-client";
import { getProductInfoFromSource } from "../eligibility/returns-coverage-eligibility";
import { reportTiming } from "../monitoring/timing";
import { FlexibleCheckoutButtons } from "../render-elements/flexible-coverage-checkout-buttons";
import { getInfoCardTheme, InfoCard } from "../render-elements/info-card";
import STYLE_MANAGER from "../style-manager";
import { isPackageProtection, isRedoItem, RedoProductToSell } from "../utils";
import {
  getCheckoutButtonItemsToAdd,
  getCheckoutOnClickEvent,
} from "../utils/checkout-buttons/on-click-logic";

export const WidgetManager = memo(function WidgetManager({
  autocheck,
  fees,
  toggles,
  setOnBuyNowCoverage,
  coverageInfo,
  redoProductToSell,
  productIdToSellingPlanId,
  cartCurrency,
  cart,
  shopifyClient,
  redoClient,
  coverageCheckoutButtonClicked,
  setCoverageCheckoutButtonClicked,
  triggerCartRefresh,
}: {
  autocheck: WidgetAutocheck;
  fees: WidgetFees;
  toggles: {
    redoToggleElements: RedoToggleElement[];
    widgetConfig?: WidgetConfig;
  }[];
  setOnBuyNowCoverage: (onClickCoverage: any) => void;
  coverageInfo: CoverageInfo;
  redoProductToSell: RedoProductToSell;
  productIdToSellingPlanId: Record<string, number>;
  cartCurrency: Currency;
  cart: ShopifyCart | undefined;
  shopifyClient: ExtensionShopifyClient;
  redoClient: ShopifyExtensionClient;
  coverageCheckoutButtonClicked: boolean;
  setCoverageCheckoutButtonClicked: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  triggerCartRefresh: () => void;
}) {
  const allowingBubblingRef = useRef<boolean>(false);
  const [bundlerDiscountedCartValue, setBundlerDiscountedCartValue] = useState<
    number | null
  >(null);
  useEffect(() => {
    document.addEventListener("bundler:total_cart_values", function (e) {
      setBundlerDiscountedCartValue(
        (e as any)?.detail?.total_amount_discounted / 100,
      );
    });
  }, []);

  const hasProtectionReturnsAndSplit =
    autocheck.packageProtection.coverageEnabled &&
    (autocheck.coverageEnabled ||
      autocheck.finalSaleReturns?.coverageEnabled) &&
    autocheck.packageProtection.splitProducts;

  const sellingPlanId =
    productIdToSellingPlanId?.[String(redoProductToSell.productId)];

  const comparePriceOrHighestPriceLoad = useLoad(async () => {
    let compareAtPrice = 0;
    for (const item of cart?.items || []) {
      if (isRedoItem(item) || isPackageProtection(item)) {
        continue;
      }
      const { product } = await getProductInfoFromSource({
        autocheck,
        cartItem: item,
        shopifyClient,
        redoClient,
      });
      const variant = product?.variants.find(
        (v) => String(v.id) === String(item.variant_id),
      );
      const preDiscountPriceCents = item.original_line_price || 0;
      const preDiscountPriceDollars = preDiscountPriceCents / 100;
      const linePriceCents = item.line_price || 0;
      const linePriceDollars = linePriceCents / 100;
      // compare at price is already in dollars
      const comparePrice = variant?.compare_at_price
        ? parseFloat(variant.compare_at_price.toString()) * item.quantity
        : 0;

      // not all products have a compare at price, so we'll fallback to a pre-discount price or line price
      // that way this variable always represents the highest price of the product that we have.
      compareAtPrice +=
        comparePrice || preDiscountPriceDollars || linePriceDollars;
    }
    return compareAtPrice;
  }, [cart?.items]);

  const cartItems = cart?.items;
  const cart_subtotal = isDefined(bundlerDiscountedCartValue)
    ? bundlerDiscountedCartValue
    : (cartItems?.reduce((acc, item) => {
        if (isRedoItem(item) || isPackageProtection(item)) {
          return acc;
        }
        return acc + (item.line_price || 0);
      }, 0) || 0) / 100;

  const pre_discount_cart_subtotal =
    (cartItems?.reduce((acc, item) => {
      if (isRedoItem(item) || isPackageProtection(item)) {
        return acc;
      }
      return acc + (item.original_line_price || 0);
    }, 0) || 0) / 100;

  const tryFirstTotalDueLater = cart
    ? getTryFirstTotals({ cart, autocheck }).dueLaterTotal
    : 0;

  const textInfo = {
    mainProductType: fees.mainType,
    hasProtectionReturnsAndSplit,
    coverageInfo,
    currency: cartCurrency,
    coveragePrice: redoProductToSell.price,
    cartTotal:
      globalThis.redoStorefront?.type === "commercecloud"
        ? (cart?.total_price ?? 0)
        : cart_subtotal,
    cartTotalPreDiscount: pre_discount_cart_subtotal,
    dueLaterTotal: tryFirstTotalDueLater,
    coverageCreditAmount: getCoverageCreditAmount(
      autocheck.coverageCreditIncentive,
      cart_subtotal,
    ),
    compareAtPrice: comparePriceOrHighestPriceLoad.value || 0,
  };

  const defaultCoverageButtonTexts =
    StorefrontCheckoutTextUtils.getCoverageTextAndSubtext(
      StorefrontCheckoutTextUtils.defaultCheckoutTexts.coverageButtonText,
      textInfo,
    );

  const defaultTextsForInfoCard =
    StorefrontCheckoutTextUtils.getCoverageTextAndSubtext(
      StorefrontCheckoutTextUtils.defaultCheckoutTexts.cardText,
      textInfo,
    );

  const defaultTextsForNonCoverageButton =
    StorefrontCheckoutTextUtils.getTextByProduct(
      StorefrontCheckoutTextUtils.defaultCheckoutTexts.nonCoverageButtonText,
      textInfo,
    );

  const hasFinalSaleCoverage =
    coverageInfo.returns.cartEligible && coverageInfo.returns.isFinalSale;

  const cartParams = new URLSearchParams();
  const itemsToAdd = getCheckoutButtonItemsToAdd({
    singleProductVariantId: redoProductToSell.variantId,
    hasFinalSaleCoverage,
    sellingPlanId: sellingPlanId ?? null,
  });

  const onClickProps = {
    autocheck,
    client: shopifyClient,
    itemsToAdd,
    cartParams,
    coverageCheckoutButtonClicked,
    setCoverageCheckoutButtonClicked,
    allowingBubblingRef,
    triggerCartRefresh,
  };

  setOnBuyNowCoverage(getCheckoutOnClickEvent(true, true, onClickProps));

  const onClickCoverage = getCheckoutOnClickEvent(true, false, onClickProps);
  const onClickNoCoverage = getCheckoutOnClickEvent(false, false, onClickProps);

  let cartReactElement = null;
  const cartReactElements: JSX.Element[] = [];

  if (isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy]) {
    cartReactElement = (
      <CheckoutButtons
        customCss={autocheck.customToggleCss}
        defaultCoverageButtonTexts={defaultCoverageButtonTexts}
        defaultNonCoverageButtonText={defaultTextsForNonCoverageButton ?? ""}
        onClickNoCoverage={onClickNoCoverage}
        onCoverageCheckoutClick={onClickCoverage}
      />
    );
  }

  for (const { redoToggleElements, widgetConfig } of toggles) {
    let element = cartReactElement;
    if (
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
      (widgetConfig?.elementType ===
        WidgetElementType.COVERAGE_CHECKOUT_BUTTON ||
        widgetConfig?.elementType ===
          WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON)
    ) {
      element = (
        <FlexibleCheckoutButtons
          cartTotal={Number(cart_subtotal)}
          cartTotalPreDiscount={pre_discount_cart_subtotal}
          compareAtPrice={comparePriceOrHighestPriceLoad.value ?? 0}
          coveragePrice={Number(redoProductToSell.price)}
          currency={cartCurrency}
          customCss={autocheck.customToggleCss}
          html={widgetConfig.content}
          isCoverage={
            widgetConfig.elementType ===
            WidgetElementType.COVERAGE_CHECKOUT_BUTTON
          }
          onClickCoverage={onClickCoverage}
          onClickNoCoverage={onClickNoCoverage}
        />
      );
    } else if (
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
      widgetConfig?.elementType === WidgetElementType.BOTH_CHECKOUT_BUTTONS
    ) {
      const coverageButtonText =
        StorefrontCheckoutTextUtils.getCoverageTextAndSubtext(
          widgetConfig.coverageButtonText,
          textInfo,
        );
      const nonCoverageButtonText =
        StorefrontCheckoutTextUtils.getTextByProduct(
          widgetConfig.nonCoverageButtonText,
          textInfo,
        );
      const coverageButtonStyles = widgetConfig.coverageButtonStyles;

      element = cartReactElement = (
        <CheckoutButtons
          coverageButtonStyles={coverageButtonStyles}
          coverageButtonText={coverageButtonText}
          customCss={autocheck.customToggleCss}
          defaultCoverageButtonTexts={defaultCoverageButtonTexts}
          defaultNonCoverageButtonText={defaultTextsForNonCoverageButton ?? ""}
          nonCoverageButtonText={nonCoverageButtonText}
          onClickNoCoverage={onClickNoCoverage}
          onCoverageCheckoutClick={onClickCoverage}
        />
      );
    } else if (widgetConfig?.elementType === WidgetElementType.INFO_CARD) {
      const titleAndSubtitle =
        StorefrontCheckoutTextUtils.getCoverageTextAndSubtext(
          widgetConfig.cardText,
          textInfo,
        );
      element = cartReactElement = (
        <InfoCard
          coverage={autocheck.coverage}
          defaultTexts={defaultTextsForInfoCard}
          packageProtectionEnabled={autocheck.packageProtection.coverageEnabled}
          packageProtectionPlusEnabled={
            autocheck.packageProtection.packageProtectionPlusEnabled
          }
          price={{
            amount: redoProductToSell.price || +fees.price,
            currency: cartCurrency,
          }}
          returnsCoverage={autocheck.coverage}
          subtitleText={titleAndSubtitle.subtitleText || ""}
          theme={getInfoCardTheme({ autocheck, widgetConfig })}
          titleText={titleAndSubtitle.titleText || ""}
        />
      );
    }

    cartReactElements.push(
      ...redoToggleElements.map((toggle, i) => (
        <StyleShadowRoot
          key={`${toggle.id}-${cartReactElements.length + i}`}
          shadowRoot={toggle.root}
          styleManager={STYLE_MANAGER}
        >
          <ModalRootContext.Provider value={toggle.modalContainer}>
            <meta found-selector={toggle.getAttribute("found-selector")} />
            {element}
          </ModalRootContext.Provider>
        </StyleShadowRoot>
      )),
    );
  }

  // Report timing for coverage button rendering
  if (cartReactElements.length > 0) {
    reportTiming("coverage.buttonRendered", {
      sendDatadogMetric: true,
      completeTiming: true,
    });
  }

  return <>{cartReactElements}</>;
});
